import { registerLocaleData } from '@angular/common';
import {
    provideHttpClient,
    withInterceptors,
    withXhr,
} from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withNavigationErrorHandler,
    withPreloading,
} from '@angular/router';
import { provideHippo } from '@hippo/hippo.provider';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { resolveInitialLang } from 'app/core/transloco/language-preference';
import { TranslocoHttpLoader } from 'app/core/transloco/transloco.http-loader';
import {
    LogLevel,
    provideAuth as provideOidcAuth,
} from 'angular-auth-oidc-client';
import { environment } from 'environments/environment';
import { lastValueFrom } from 'rxjs';
import { MaintenanceService } from './core/maintenance/maintenance.service';
import { HippoSplashScreenService } from '@hippo/services/splash-screen';
import { apiInterceptor } from './core/services/api.interceptor';
import { LanguageService } from './core/services/language.service';
import { ModuleConfigService } from './core/services/module-config.service';

import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
    provideHttpCache,
    withHttpCacheInterceptor,
    withLocalStorage,
} from '@ngneat/cashew';
import * as Sentry from '@sentry/angular';
import {
    CUSTOM_DATE_FORMATS,
    CustomDatePickerAdapter,
} from './core/services/custom-date-adapter.service';
import { PwaService } from './core/services/pwa.service';
import { resetTimerInterceptor } from './core/services/reset-timer.interceptor';

registerLocaleData(localeFr, 'en', localeFrExtra);
registerLocaleData(localeDe, 'de', localeDeExtra);
registerLocaleData(localeEn, 'orgen', localeEnExtra);

/**
 * Kann eine Lazy-Route ihren Chunk nicht laden (typisch: die Seite läuft noch
 * auf einem alten Build, dessen Dateien nach einem Deployment nicht mehr
 * existieren — besonders in Safari, das Tabs lange eingefroren hält), bleibt
 * die Navigation sonst in einer endlosen Ladeschleife hängen (HIP-79).
 * Einmaliger Reload holt den frischen Build; der Zeitstempel verhindert eine
 * Reload-Schleife, falls auch der neue Build nicht ladbar ist.
 */
const CHUNK_RELOAD_KEY = 'stale_chunk_reload_at';
function reloadOnStaleChunk(error: unknown): void {
    const message = String(
        (error as { error?: { message?: string } })?.error?.message ??
            (error as { message?: string })?.message ??
            error ??
            ''
    );
    const staleChunk =
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('ChunkLoadError');
    if (!staleChunk) {
        return;
    }
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReload < 30000) {
        return;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
}

export const appConfig: ApplicationConfig = {
    providers: [
        PwaService,
        provideAnimations(),
        provideHttpClient(
            withXhr(),
            withInterceptors([
                apiInterceptor,
                withHttpCacheInterceptor(),
                resetTimerInterceptor,
            ])
        ),
        provideHttpCache(withLocalStorage()),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withNavigationErrorHandler(event => reloadOnStaleChunk(event))
            // withHashLocation()
        ),
        { provide: DateAdapter, useClass: CustomDatePickerAdapter },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },

        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'de',
                        label: 'Deutsch',
                    },
                    {
                        id: 'en',
                        label: 'English',
                    },
                ],
                defaultLang: 'de',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
                missingHandler: {
                    // Off on purpose: transloco loads `<scope>/en` next to
                    // `<scope>/de` for every single scope when this is on, so
                    // it doubled the System_translations.php requests. The
                    // backend serves both languages from the same key set —
                    // measured across all 36 scopes, de and en hold the exact
                    // same 1876 keys with no empty German value — so the
                    // fallback never actually rescued a key (HIP-53).
                    useFallbackTranslation: false,
                    logMissingKey: true,
                },
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const moduleConfigService = inject(ModuleConfigService);
            return moduleConfigService.load();
        }),
        provideAppInitializer(() => {
            const initializerFn = (() => {
                const translocoService = inject(TranslocoService);
                inject(LanguageService).restore();
                // Start in the last picked language instead of always in the
                // default one — otherwise the sign-in page (and with it the
                // Cognito hosted UI) is always German (HIP-43).
                const initialLang = resolveInitialLang(translocoService);
                translocoService.setActiveLang(initialLang);
                return () => lastValueFrom(translocoService.load(initialLang));
            })();
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const maintenanceService = inject(MaintenanceService);
            const splashScreenService = inject(HippoSplashScreenService);
            const translocoService = inject(TranslocoService);
            const initialLang = resolveInitialLang(translocoService);
            return Promise.all([
                maintenanceService.checkMaintenance(),
                lastValueFrom(translocoService.load(`codext/${initialLang}`)),
            ]).then(() => {
                if (maintenanceService.isActive()) {
                    splashScreenService.hide();
                }
            });
        }),
        provideAuth(),
        provideOidcAuth({
            config: {
                authority: environment.cognito.authority,
                redirectUrl: environment.cognito.redirectUrl,
                postLogoutRedirectUri:
                    environment.cognito.postLogoutRedirectUri,
                clientId: environment.cognito.clientId,
                scope: environment.cognito.scope,
                responseType: environment.cognito.responseType,
                silentRenew: true,
                useRefreshToken: true,
                renewTimeBeforeTokenExpiresInSeconds: 30,
                logLevel: environment.production
                    ? LogLevel.None
                    : LogLevel.Warn,
            },
        }),
        provideIcons(),
        provideHippo({
            hippo: {
                layout: 'classic',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                ],
            },
        }),
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
    ],
};
